import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@jq4race/i18n';
import {
  type RequestPasswordResetRequest,
  RequestPasswordResetRequestSchema,
} from '@jq4race/shared';
import { Button, Input, Label } from '@jq4race/ui';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { useAuthServices } from '../auth-services-context';
import { AuthShell } from '../components/AuthShell';
import { ErrorBanner } from '../components/ErrorBanner';

export function ForgotPasswordPage() {
  const { t } = useTranslation('auth');
  const auth = useAuthServices();
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState(false);

  const form = useForm<RequestPasswordResetRequest>({
    resolver: zodResolver(RequestPasswordResetRequestSchema),
    defaultValues: { email: '' },
  });

  async function onSubmit(values: RequestPasswordResetRequest) {
    setError(null);
    try {
      await auth.requestPasswordReset(values.email);
      setDone(true);
    } catch (err) {
      setError(err);
    }
  }

  if (done) {
    return (
      <AuthShell
        title={t('forgotPassword.done.title')}
        eyebrow={t('forgotPassword.eyebrow')}
        description={t('forgotPassword.done.description')}
        aside={{
          illustration: 'letter',
          eyebrow: t('forgotPassword.aside.eyebrow'),
          headline: t('forgotPassword.aside.headline'),
          body: t('forgotPassword.done.aside.body'),
        }}
        footer={
          <Link to="/login" className="font-medium text-jq-navy underline underline-offset-2">
            {t('shared.backToSignIn')}
          </Link>
        }
      >
        <p className="text-[14px] text-jq-graphite">{t('forgotPassword.done.body')}</p>
      </AuthShell>
    );
  }

  const { errors, isSubmitting } = form.formState;

  return (
    <AuthShell
      title={t('forgotPassword.title')}
      eyebrow={t('forgotPassword.eyebrow')}
      description={t('forgotPassword.description')}
      aside={{
        illustration: 'letter',
        eyebrow: t('forgotPassword.aside.eyebrow'),
        headline: t('forgotPassword.aside.headline'),
        body: t('forgotPassword.aside.body'),
      }}
      footer={
        <Link to="/login" className="font-medium text-jq-navy underline underline-offset-2">
          {t('shared.backToSignIn')}
        </Link>
      }
    >
      <form className="grid gap-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
        <ErrorBanner error={error} />
        <div className="grid gap-2">
          <Label htmlFor="email">{t('forgotPassword.emailLabel')}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder={t('forgotPassword.emailPlaceholder')}
            aria-invalid={!!errors.email}
            {...form.register('email')}
          />
          {errors.email ? (
            <p className="text-[13px] text-jq-danger">{errors.email.message}</p>
          ) : null}
        </div>
        <Button type="submit" disabled={isSubmitting} className="w-full">
          {isSubmitting ? t('forgotPassword.submitting') : t('forgotPassword.submit')}
        </Button>
      </form>
    </AuthShell>
  );
}
